<script setup lang="ts">
import { type EditorState, INSERT_PARAGRAPH_COMMAND, type LexicalEditor, type SerializedEditorState } from "lexical";
import {
  LexicalAutoLinkPlugin,
  LexicalLinkPlugin,
  LexicalListPlugin,
  LexicalMarkdownShortcutPlugin,
} from "lexical-vue";
import { computed, nextTick, onUnmounted, ref, watch } from "vue";

import ChatExtras from "~/components/chat/ChatExtras.vue";
import Button from "~/components/dumb/Button.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import SpeechToTextControls from "~/components/SpeechToTextControls.vue";
import { THROTTLE_MS } from "~/constants/app";
import { SendIcon } from "~/icons";
import { ButtonSize, ButtonStyle, CommandId } from "~/shared/enums";
import type { Chat, ChatMessage, LinkUpdate, Skill } from "~/shared/types";
import { useDataStore, usePageStore } from "~/stores";
import { areLexicalStatesSame, isLexicalStateEmpty, makeDuid, makeEmptyLexicalState } from "~/utils/common";
import { ThrottleManager } from "~/utils/throttleManager";
import { when } from "~/utils/wait";

import { URL_MATCHERS } from "./const";
import ChatExtrasManagerPlugin from "./plugins/ChatExtrasManagerPlugin.vue";
import CheckListPlugin from "./plugins/CheckListPlugin.vue";
import CodeHighlightPlugin from "./plugins/CodeHighlightPlugin.vue";
import CodeSelectorPlugin from "./plugins/CodeSelectorPlugin.vue";
import ColorPlugin from "./plugins/ColorPlugin.vue";
import EnterPlugin from "./plugins/EnterPlugin.vue";
import LinkPlugin from "./plugins/LinkPlugin.vue";
import ManageTextFormatPlugin from "./plugins/ManageTextFormatPlugin.vue";
import OnChangePlugin from "./plugins/OnChangePlugin.vue";
import PasteUrlPlugin from "./plugins/PasteUrlPlugin.vue";
import PlaceholderPlugin from "./plugins/PlaceholderPlugin.vue";
import StreamTextPlugin from "./plugins/StreamTextPlugin.vue";
import TabPlugin from "./plugins/TabPlugin.vue";
import TextInsertPlugin from "./plugins/TextInsertPlugin.vue";
import AiChatTypeaheadPlugin from "./plugins/typeahead/AiChatTypeaheadPlugin.vue";
import TextEditor from "./TextEditor.vue";
import { TRANSFORMERS } from "./transformers";
import { PARTIAL_DART_EDITOR_NODES } from "./utils";

const props = defineProps<{
  // Chat mode props
  chat?: Chat;
  message?: ChatMessage;
  isDraft?: boolean;
  // Skill mode props
  skill?: Skill;
  // Common props
  editorClasses?: string;
}>();

const emit = defineEmits<{
  send: [message: ChatMessage];
  streamUpdated: [];
}>();

const dataStore = useDataStore();
const pageStore = usePageStore();

const chatExtras = ref<InstanceType<typeof ChatExtras> | null>(null);
const textEditor = ref<InstanceType<typeof TextEditor> | null>(null);
const streamTextPlugin = ref<InstanceType<typeof StreamTextPlugin> | null>(null);
const chatExtrasManagerPlugin = ref<InstanceType<typeof ChatExtrasManagerPlugin> | null>(null);
const typeaheadPlugin = ref<InstanceType<typeof AiChatTypeaheadPlugin> | null>(null);
const speechToText = ref<InstanceType<typeof SpeechToTextControls> | null>(null);
const textInsertPlugin = ref<InstanceType<typeof TextInsertPlugin> | null>(null);

const isChat = computed(() => props.chat !== undefined);

const initialState = ref((isChat.value ? props.message?.content : props.skill?.prompt) ?? makeEmptyLexicalState());
const content = ref(initialState.value);
const isEmpty = ref(isLexicalStateEmpty(content.value));

const namespace = computed(() =>
  isChat.value ? `message-${props.isDraft}-${props.message?.duid}` : `skill-${props.skill?.duid}`
);

const isEditable = computed(() => (isChat.value ? props.isDraft : true));

const hasFocus = computed(() => textEditor.value?.hasFocus ?? false);
const isReady = computed(() => textEditor.value?.isReady ?? false);
const isStreaming = computed(() => streamTextPlugin.value?.isStreaming ?? false);

const placeholder = computed(() =>
  isChat.value
    ? props.isDraft
      ? "Plan, search, and create anything"
      : ""
    : "Write what you want Dart AI to do when you use this skill"
);

// Save managers for both modes
const saveManager = new ThrottleManager(
  (
    value: SerializedEditorState,
    chat: Chat | undefined,
    message: ChatMessage | undefined,
    isDraft: boolean | undefined,
    skill: Skill | undefined
  ) => {
    if (skill) {
      dataStore.updateSkill({ duid: skill.duid, prompt: value });
      return;
    }

    if (!isDraft || !message || !chat) {
      return;
    }

    const latestDraft = dataStore.getChatByDuid(chat.duid)?.draft;
    const draftToSave = latestDraft ? { ...latestDraft, content: value } : { ...message, content: value };
    dataStore.updateChat({ duid: chat.duid, draft: draftToSave });
  },
  THROTTLE_MS
);

const onEditorChange = (newEditorState: EditorState) => {
  const value = newEditorState.toJSON();
  content.value = value;

  if (areLexicalStatesSame(value, initialState.value)) {
    return;
  }

  saveManager.run(value, props.chat, props.message, props.isDraft, props.skill);
};

const setIsEmpty = (newEditorState: EditorState) => {
  const value = newEditorState.toJSON();
  isEmpty.value = isLexicalStateEmpty(value);
};

const focus = async () => {
  await when(isReady);
  textEditor.value?.focus();
};

// Chat-specific functions
const sendMessage = () => {
  if (isEmpty.value || !props.message || !isChat.value || speechToText.value?.working) {
    return;
  }

  emit("send", {
    duid: makeDuid(),
    content: content.value,
    extras: props.message.extras,
    isAssistant: false,
    createdAt: new Date().toISOString(),
  });

  const emptyState = makeEmptyLexicalState();
  textEditor.value?.setEditorState(emptyState);
  saveManager.run(emptyState, props.chat, props.message, props.isDraft, props.skill);
  saveManager.finish();
  nextTick(focus);
};

const onEnter = (event: KeyboardEvent, editor: LexicalEditor) => {
  if (event.shiftKey) {
    editor.dispatchCommand(INSERT_PARAGRAPH_COMMAND, undefined);
    return;
  }
  sendMessage();
};

const streamText = (markdown: string) => {
  streamTextPlugin.value?.replaceAll(markdown);
};

const onSpeechText = (markdown: string) => {
  textInsertPlugin.value?.insert(markdown);
};

const handleLinkChanges = (creates: string[], destroys: string[], updates: LinkUpdate[]) =>
  chatExtrasManagerPlugin.value?.handleLinkChanges(creates, destroys, updates);

// Skill-specific functions
const insertSkill = (skill: Skill) => {
  typeaheadPlugin.value?.insertSkill(skill);
  focus();
};

// Watchers
watch(
  () => (isChat.value ? props.chat?.duid : props.skill?.duid),
  (duid) => {
    if (!duid) {
      return;
    }
    const lexicalContent = isChat.value
      ? dataStore.getChatByDuid(duid)?.draft?.content
      : dataStore.getSkillByDuid(duid)?.prompt;

    if (!lexicalContent || (isChat.value && !props.isDraft)) {
      return;
    }

    saveManager.finish();
    textEditor.value?.setEditorState(lexicalContent);
  }
);

watch(
  () => props.skill?.prompt,
  (newText) => {
    if (!newText) {
      return;
    }
    saveManager.finish();
    textEditor.value?.setEditorState(newText);
  }
);

onUnmounted(() => {
  saveManager.finish();
});

defineExpose({
  focus,
  isReady,
  isStreaming,
  streamText,
  syncExtras: (extrasAreUserManaged?: boolean) => chatExtras.value?.syncExtras(extrasAreUserManaged),
});
</script>

<template>
  <TextEditor
    ref="textEditor"
    :namespace="namespace"
    :nodes="PARTIAL_DART_EDITOR_NODES"
    :initial-state="initialState"
    :placeholder="placeholder"
    :disabled="!isEditable"
    extra-rounded
    :top-margin-px="2"
    :left-margin-px="isEditable ? 8 : 0"
    :right-margin-px="isEditable ? 8 : 0"
    :max-height-px="isChat && isEditable ? 300 : undefined"
    :min-height-px="!isChat ? 200 : undefined"
    :always-borderless="isChat && !isEditable"
    :editor-classes="editorClasses">
    <template v-if="isChat" #before>
      <ChatExtras
        v-if="message?.extras"
        ref="chatExtras"
        :chat="chat!"
        :extras="message.extras"
        :is-draft="!!isDraft"
        @insert-skill="insertSkill" />
    </template>

    <template #default>
      <!-- lexical-vue plugins -->
      <LexicalAutoLinkPlugin :matchers="URL_MATCHERS" />
      <LexicalLinkPlugin />
      <LexicalListPlugin />
      <LexicalMarkdownShortcutPlugin :transformers="TRANSFORMERS" />
      <!-- custom plugins -->
      <AiChatTypeaheadPlugin v-if="!pageStore.isMobile" ref="typeaheadPlugin" />
      <AiChatTypeaheadPlugin v-if="!pageStore.isMobile" />
      <ChatExtrasManagerPlugin
        v-if="isChat && isDraft && chat"
        ref="chatExtrasManagerPlugin"
        :chat-duid="chat.duid"
        :is-empty="isEmpty" />
      <CheckListPlugin />
      <CodeHighlightPlugin />
      <CodeSelectorPlugin :editable="isEditable" />
      <ColorPlugin />
      <EnterPlugin v-if="isChat && !pageStore.isMobile" @enter="onEnter" />
      <TextInsertPlugin v-if="isChat" ref="textInsertPlugin" />
      <LinkPlugin @link-changes="handleLinkChanges" />
      <ManageTextFormatPlugin />
      <OnChangePlugin :has-focus="hasFocus" @change="onEditorChange" @unfocused-change="setIsEmpty" />
      <PasteUrlPlugin />
      <PlaceholderPlugin v-if="isEditable" no-ai />
      <StreamTextPlugin v-if="isChat && !message" ref="streamTextPlugin" @updated="emit('streamUpdated')" />
      <TabPlugin />
    </template>
    <template v-if="isChat" #after>
      <div v-if="isDraft" class="flex w-full cursor-text items-center justify-end gap-2 pb-2.5 pr-2.5 pt-1">
        <SpeechToTextControls ref="speechToText" @result="onSpeechText" />
        <Tooltip v-if="!speechToText?.working" :command-id="CommandId.SAVE_CHAT">
          <Button
            :btn-style="ButtonStyle.RECOMMENDATION"
            :icon="SendIcon"
            :size="ButtonSize.SMALL"
            a11y-label="Send chat message"
            class="px-1.5 py-[3px]"
            :disabled="isEmpty"
            @click="sendMessage" />
        </Tooltip>
      </div>
    </template>
  </TextEditor>
</template>
